import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FilterX } from 'lucide-react';
import type { DataTableSearchConfig } from '../types';
import { TableToolbarSearch } from './TableToolbarSearch';

export interface DataTableToolbarProps {
  onClearFilters: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchConfig?: DataTableSearchConfig;
  hasActiveFilters: boolean;
  /**
   * Whether the table has any filterable column at all. When it has none the
   * "Clear filters" control is omitted rather than rendered permanently
   * disabled — there is nothing it could ever clear (KIR-3800 security groups).
   */
  hasFilterableColumns: boolean;
  /** Caller-owned controls rendered before "Clear filters" (right side). */
  endSlot?: ReactNode;
  /** KIR-4089 "Columns" customize control, rendered just before "Clear filters". */
  columnsControl?: ReactNode;
}

/**
 * Toolbar component for DataTable with filter actions.
 * Displays above the table and provides controls for filter management.
 */
export function DataTableToolbar({
  onClearFilters,
  searchValue,
  onSearchChange,
  searchConfig,
  hasActiveFilters,
  hasFilterableColumns,
  endSlot,
  columnsControl,
}: DataTableToolbarProps) {
  return (
    // KIR-5057: wrap when the end-slot controls do not fit beside the search;
    // the card wrapper is overflow-hidden, so without this they get clipped.
    <div className="flex flex-wrap items-center justify-between gap-y-2 px-4 py-2.5 border-b bg-muted/40 dark:bg-muted/30 backdrop-blur-sm">
      <TableToolbarSearch
        value={searchValue}
        onChange={onSearchChange}
        searchConfig={searchConfig}
      />
      <div className="ml-auto flex items-center gap-3">
        {endSlot}
        {columnsControl}
        {hasFilterableColumns && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                disabled={!hasActiveFilters}
                className="h-8 px-3 shadow-sm hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-all group"
              >
                <FilterX className="mr-2 h-3.5 w-3.5 group-hover:text-destructive transition-colors" />
                <span className="text-xs font-medium group-hover:text-destructive transition-colors">
                  Clear filters
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Remove all active filters</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
